import React from "react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import * as Yup from "yup"
import pluralize from "pluralize"
import { EMAIL_VALIDATION_REGEX } from "utilities/constants"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import TextField from "components/ReactHookForm/TextField"
import TextArea from "components/ReactHookForm/TextArea"
import AlertBox, { AlertBoxType } from "components/elements/AlertBox/AlertBox"
import Icon from "components/elements/Icon"
import { assignLeadRetrievalLicense } from "api/events"
import { useLeadRetrievalLicenses, leadRetrievalLicensesCacheKey } from "queries/events"
import { successToast } from "utilities/toasts"

const schema = Yup.object().shape({
  email: Yup.string().matches(EMAIL_VALIDATION_REGEX, "Invalid email format").required(),
  message: Yup.string(),
})

type Props = {
  eventId: string
  onHide: () => void
}

const InviteExhibitorOrAssignLicense = ({ eventId, onHide }: Props) => {
  const queryClient = useQueryClient()
  const { data: summary } = useLeadRetrievalLicenses(eventId)
  const remainingLicenses = summary?.remaining ?? 0

  const handleSubmit = async ({ email, message }: { email: string, message: string }) => {
    await assignLeadRetrievalLicense(eventId, email, message || undefined)
    await queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(eventId))
    successToast({ title: `Invite sent to ${email}` })
    onHide()
  }

  return (
    <ModalForm
      initialValues={{ email: "", message: "" }}
      validationSchema={schema}
      onSubmit={handleSubmit}
      onClose={onHide}
      title="Assign License to Exhibitor"
      submitText="Assign & Send Invite"
      submitIsDisabled={remainingLicenses <= 0}
      footerLeft={<Text color="gray.500">{remainingLicenses} {pluralize("license", remainingLicenses)} remaining</Text>}
      size="lg"
    >
      {({ control }) => (
        <Stack spacing={0}>
          <Text mb={4}>
            Assign one of the event&apos;s available licenses to an exhibitor. Diobox emails
            the exhibitor an invitation to activate the license. No payment is required from
            the exhibitor.
          </Text>
          <TextField
            vertical
            label="Exhibitor Email"
            name="email"
            control={control}
            variant="outline"
            placeholder="Enter email address"
          />
          <TextArea
            vertical
            label={(
              <>
                Message <Text as="span" fontWeight="normal" color="gray.500">(optional)</Text>
              </>
            )}
            name="message"
            control={control}
            placeholder="Add a note to the invitation email"
          />
          <Box mt={3}>
            <AlertBox
              type={AlertBoxType.Upgrade}
              text={(
                <Flex gap={3} alignItems="flex-start">
                  <Icon icon="info" color="blue.500" mt={1} />
                  <Stack spacing={1}>
                    <Text fontWeight="semibold">What happens next?</Text>
                    <Stack spacing={3}>
                      <Text>
                        The exhibitor receives an email invitation to create an account and
                        activate the license. The exhibitor is listed
                        as <Text as="span" fontWeight="bold">Invited</Text> until activation
                        is complete.
                      </Text>
                      <Text>
                        Deleting the exhibitor before activation releases the license back to
                        the event.
                      </Text>
                    </Stack>
                  </Stack>
                </Flex>
              )}
            />
          </Box>
        </Stack>
      )}
    </ModalForm>
  )
}

export default InviteExhibitorOrAssignLicense
